import { useEffect } from "react";
import { Shield } from "lucide-react";
import { useTranslation } from "react-i18next";
import LoadingComponent from "../../components/LoadingComponent";
import { useRolesData } from "../roles/hooks/useRolesData";
import { useRolesModals } from "../roles/hooks/useRolesModals";
import {
  RolesFilters,
  RolesGrid,
  RolesHeader,
  RolesTable,
} from "../roles/components";
import RoleModals from "../roles/components/RoleModals";
import Pagination from "../roles/components/Pagination";
import { useAuth } from "../../context/AuthContext";
import { hasPermission, PERMISSIONS } from "../../utils/permissions";

const RolesManagementPage = () => {
  const { t } = useTranslation();

  const { permissions } = useAuth();
  // Check if user has permission to create or edit products
  const canCreate = hasPermission(permissions, PERMISSIONS.CREATE_ROLES);
  const canEdit = hasPermission(permissions, PERMISSIONS.EDIT_ROLES);
  const canDelete = hasPermission(permissions, PERMISSIONS.DELETE_ROLES);

  // User can add category if they have either create or edit permission
  const canAddEdit = canCreate || canEdit;
  // Custom hooks for data and modals
  const {
    filtered,
    loading,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    viewMode,
    setViewMode,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    paginatedData,
    handleToggleStatus,
    handleSeedDefaults,
    refreshRoles,
  } = useRolesData();

  const {
    modals,
    currentRole,
    formData,
    saving,
    permissionSearch,
    setPermissionSearch,
    roleUsers,
    loadingUsers,
    updateFormData,
    openCreateModal,
    openEditModal,
    openPermissionsModal,
    openUsersModal,
    openDeleteModal,
    openDuplicateModal,
    closeModal,
    handleCreate,
    handleUpdate,
    handleDelete,
    handleUpdatePermissions,
    handleDuplicate,
    toggleModulePermission,
    toggleAllModulePermissions,
    selectAllPermissions,
    clearAllPermissions,
  } = useRolesModals(refreshRoles);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setViewMode("cards");
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [setViewMode]);

  if (loading) {
    return <LoadingComponent title={t("Roles")} />;
  }

  const { totalPages, startIndex, endIndex, paginatedItems } = paginatedData;

  return (
    <div className="min-h-screen bg-[#f2e6db] py-4 sm:py-8 px-4">
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideDown { from { opacity: 0; transform: translateY(-20px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      <div className="mx-auto">
        <div
          className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 mb-4 sm:mb-6"
          style={{ animation: "fadeIn 0.5s ease-out" }}
        >
          <RolesHeader
            rolesCount={filtered.length}
            onSeedDefaults={handleSeedDefaults}
            onCreateNew={openCreateModal}
            canAddEdit={canAddEdit}
          />

          <RolesFilters
            search={search}
            setSearch={setSearch}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            viewMode={viewMode}
            setViewMode={setViewMode}
          />

          <div className="p-2 sm:p-6">
            {filtered.length === 0 ? (
              <div className="text-center py-12">
                <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">{t("No roles found")}</p>
                <p className="text-gray-400 text-sm mt-2">
                  {t("Try adjusting your search or filters")}
                </p>
              </div>
            ) : viewMode === "cards" ? (
              <RolesGrid
                roles={paginatedItems}
                canEdit={canEdit}
                canDelete={canDelete}
                onEdit={openEditModal}
                onPermissions={openPermissionsModal}
                onUsers={openUsersModal}
                onDuplicate={openDuplicateModal}
                onDelete={openDeleteModal}
                onToggleStatus={handleToggleStatus}
              />
            ) : (
              <RolesTable
                roles={paginatedItems}
                canEdit={canEdit}
                canDelete={canDelete}
                onEdit={openEditModal}
                onPermissions={openPermissionsModal}
                onUsers={openUsersModal}
                onDuplicate={openDuplicateModal}
                onDelete={openDeleteModal}
                onToggleStatus={handleToggleStatus}
              />
            )}
          </div>
          {!!totalPages && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              itemsPerPage={itemsPerPage}
              totalItems={filtered.length}
              startIndex={startIndex}
              endIndex={endIndex}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
            />
          )}
        </div>
      </div>

      <RoleModals
        modals={modals}
        currentRole={currentRole}
        formData={formData}
        saving={saving}
        permissionSearch={permissionSearch}
        setPermissionSearch={setPermissionSearch}
        roleUsers={roleUsers}
        loadingUsers={loadingUsers}
        updateFormData={updateFormData}
        onClose={closeModal}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        onUpdatePermissions={handleUpdatePermissions}
        onDuplicate={handleDuplicate}
        toggleModulePermission={toggleModulePermission}
        toggleAllModulePermissions={toggleAllModulePermissions}
        selectAllPermissions={selectAllPermissions}
        clearAllPermissions={clearAllPermissions}
      />
    </div>
  );
};

export default RolesManagementPage;
